import * as yup from 'yup';
import { checkNullOrUndefined, emptySchema, normalizeCatalogOption, resolveFobCost, resolveFobDisplay } from 'app/main/apps/common/AuraFunctions';
import { productSupplierInitValue } from './productSupplier';

export function productInitValues(data) {
  return {
    id: data && data.id ? data.id : null,
    name: data && data.name ? data.name : '',
    description: data && data.description ? data.description : '',
    code: data && data.code ? data.code : '',
    type: data && data.type ? data.type : { name: '' },
    // PGSS-5280: Sub Type is optional — never invent { name: '' } for a clear (truthy empty object
    // made ProductService.updateProduct leave the existing sub_type_id alone). Type / Stone Color
    // stay required and keep their existing defaults.
    sub_type: normalizeCatalogOption(data?.sub_type),
    stone_color: data && data.stone_color ? data.stone_color : '',
    category: data && data.category ? data.category : { name: '' },
    length: data && data.length ? data.length : '',
    width: data && data.width ? parseFloat(data.width) : '',
    height: data && data.height ? data.height : '',
    weight: data && data.weight ? data.weight : '',
    polish: data && data.polish ? data.polish : '',
    finish: data && data.finish ? data.finish : '',
    unit_cost: data && (data.unit_cost || Number(data?.unit_cost) === 0) ? data.unit_cost : '',
    unit_price: data && (data.unit_price || Number(data?.unit_price) === 0) ? parseFloat(data.unit_price) : '',
    units_in_stock: data?.units_in_stock ??  '', // Fixed: Allow 0, return '' only if null/undefined
    on_order: data?.on_order ?? '', // Fixed: Allow 0, return '' only if null/undefined
    committed: data?.committed ?? '', // Fixed: Allow 0, return '' only if null/undefined
    reorder_point: data?.reorder_point ?? '', // Fixed: Allow 0, return '' only if null/undefined
    min_package_qty: data?.min_package_qty ?? '', // PGSS-5344: supplier pack size; '' clears it
    supplier: productSupplierInitValue(data), // PGSS-5735: { id, name }, inventory products only
    short_fall: data?.short_fall ?? '', // Fixed: Allow 0, return '' only if null/undefined
    sku: data && data.sku ? data.sku : '',
    note: data && data.note ? data.note : '',
    is_taxable: data && data.is_taxable ? data.is_taxable : false,
    visible_to_wholesaler: data && data.visible_to_wholesaler ? data.visible_to_wholesaler : false,
    is_stock_product : data && data.is_stock_product ? data.is_stock_product : false,
    is_active: data && data.is_active ? data.is_active : false,
    images: data && data.images ? data.images : [],
    new_images: data && data.new_images ? data.new_images : [],
    tariff: data && data?.tariff != null ? parseFloat(data.tariff) : '', // Fixed: Allow 0, return '' only if null/undefined
    shipping: data && data?.shipping != null ? parseFloat(data.shipping) : '', // Fixed: Allow 0, return '' only if null/undefined
    fob: data && data?.fob != null ? parseFloat(data.fob) : '', // Fixed: Allow 0, return '' only if null/undefined
    margin: data && data?.margin != null ? parseFloat(data.margin) : '', // Fixed: Allow 0, return '' only if null/undefined
    order_quantity: data && data.order_quantity ? data.order_quantity : '',
    orders: data?.orders || [],
    is_custom: data?.is_custom,
    discount: data?.discount,
    quantity: data?.quantity,
    customer_provider_number: data?.customer_provider_number,
    comments: data?.comments,
    decedent: data?.decedent,
    square_feet: data?.square_feet ?? '',
    price_per_square_feet: data && data.price_per_square_feet ? data.price_per_square_feet : '',
    is_data_editted: data?.is_data_editted ?? null, // Added this field to notify BE that this product is editted.
    // Instruction for backend: delete stone color image when this flag is true
    delete_stone_color_image:  null,
    total_quantity: data?.total_quantity ?? '',
  };
}

const validations = (mode = '') => {
  const message = mode !== 'Order quantity' ? `You must enter a ${mode?.toLowerCase()}` : `${mode} can not be empty`
  if(mode === 'Length' || mode === 'Width' || mode === 'Height') {
    return yup.number()
    .positive()
    .min(0.1, `${mode} must be a positive value.`)
    .typeError(message)
    .required(message)
  }
  if(mode === 'Order quantity') {
    return yup.number()
    .positive(`${mode} must be a positive value.`)
    .integer(`Invalid ${mode}`)
    .min(1, `Min  ${mode} is 1`)
    .typeError(message)
    .required(message)
  }
  return yup.number()
    .positive()
    .min(1, `${mode} must be a positive value.`)
    .typeError(message)
    .required(message)
}

const marginValidations = (isMandatory) => {
  let yupMarginValidation =  yup.number()
  .nullable()
  .transform((value) => (Number.isNaN(value) ? undefined : value))
  .positive("Min margin value is 0")
  .min(0, "Min margin value is 0")
  .max(100, "Max margin value is 100")
  .test({
    message: "Max 2 digits after decimal or less",
    test: (val) => {
      if (val && val > 0 && !(/^\d+(\.\d{1,2})?$/.test(val))) {
        return false;
      }
      return true;
    },
  })
  yupMarginValidation = isMandatory ? yupMarginValidation.required("You must enter a margin") : yupMarginValidation;
  return yupMarginValidation;
}

const unitPriceValidations = (isMandatory, fobBasedMinValue = true) => {
  let yupUnitPriceValidation =  yup.number()
  .transform((value) => (Number.isNaN(value) ? undefined : value))
  .positive("Min value is 0")
  .min(0, "Min value is 0")
  .test({
    message: "Max 2 digits after decimal or less",
    test: (val) => {
      if (val && val > 0 && !(/^\d+(\.\d{1,2})?$/.test(val))) {
        return false;
      }
      return true;
    },
  })
  yupUnitPriceValidation = fobBasedMinValue === true ? yupUnitPriceValidation.test({
    message: "Unit price must be greater than 0",
    test: (val, { parent }) => {
      const unitPrice = val;
      const fobPrice = parent?.unit_cost;
      if ( fobPrice && Number(fobPrice) > 0 &&
        (Number.isNaN(unitPrice) || unitPrice === "" || unitPrice === "" || checkNullOrUndefined(unitPrice) || Number(unitPrice) <= 0) ) {
        return false;
      }
      return true;
    },
  }) : yupUnitPriceValidation;
  yupUnitPriceValidation = isMandatory ? yupUnitPriceValidation.required("You must enter a unit price") : yupUnitPriceValidation;
  return yupUnitPriceValidation;
}

/**
 * PGSS-5156: customer custom-product cost write helper.
 *
 * Rule: customer RFQ/Quote/Order custom products → persist cost on `fob`;
 *       supplier orders → keep writing `unit_cost` (PGSS-5006) and must not use this helper.
 *
 * The dialog's single cost input is labelled "FOB", so what the user edited is the FOB — it is sent as
 * `fob`. The product's existing `unit_cost` is preserved from the loaded product rather than the form,
 * because supplier order lines are priced from `unit_cost` (SupplierOrderUpdateService, PGSS-5006) and
 * writing the FOB into it would silently change what suppliers are paid.
 *
 * A new custom product has no loaded product, so `unit_cost` is null and the backend's fallback
 * (ProductRequest: unit_cost null -> take the FOB) sets both — the migration's end state.
 *
 * Used by both API save and local Update (`saveProductLocally`) in ProductDialog.
 *
 * @param formValues        the submitted form (its `unit_cost` field is the FOB the user sees)
 * @param existingProduct   the product the dialog was opened on, or undefined for a new one
 */
export function buildProductCostPayload(formValues, existingProduct) {
  const entered = formValues?.unit_cost;
  // PGSS-5377: FOB is optional on the Order page (orderCustomProductSchema / editCustomProductSchema
  // carry no unit_cost rule), so it can be submitted empty. Sending that as null is indistinguishable
  // from "never had a FOB", and the read then falls back to the product's untouched unit_cost — which
  // is why a cleared FOB kept showing its old value. Persist an explicit 0 so "cleared" is a value.
  //
  // Only when editing a PERSISTED product. Add Custom Product opens the dialog with
  // `productInitValues()` (ProductsTab.js:331) — a fully-populated object with `id: null`, which is
  // truthy — so gating on the object itself would send 0 for a brand-new product and defeat the
  // backend's two null-FOB fallbacks (ProductRequest's unit_cost-null copy, and
  // ProductService.addProduct's findFobForCustomProduct lookalike). Gate on the id.
  //
  // PGSS-5673: and only when the box OPENED with a value. A box that opened empty (no FOB and no
  // unit_cost, e.g. right after Make It In Review reset the pricing) and is saved empty was never
  // cleared; sending 0 there turned "no cost yet" into a permanent "cleared" FOB, which kept showing
  // 0 after the supplier re-quoted into unit_cost.
  const cleared = checkNullOrUndefined(entered) || entered === '';
  const openedWithValue = Number(resolveFobDisplay(existingProduct)) > 0;
  return {
    fob: cleared && existingProduct?.id && openedWithValue ? 0 : entered,
    unit_cost: existingProduct?.unit_cost ?? null,
  };
}

/**
 * Form Init Values
 */
export default function getInitValues(type, data) {
  // PGSS-5156: the custom-product dialogs render one cost input, named unit_cost but LABELLED "FOB".
  // Pre-filling it is a READ, so it shows the resolved cost — the product's FOB where it has one, rather
  // than the raw unit_cost (order 23980: fob 120, unit_cost 100, dialog showed 100).
  //
  // Where that value is WRITTEN is a separate decision, made in ProductDialog's submit: it goes to `fob`,
  // and the product's existing unit_cost is preserved. Seeding alone would have persisted the FOB over
  // unit_cost, which is why an earlier attempt at this was reverted — and it would have moved supplier
  // pricing, which reads unit_cost (PGSS-5006).
  if (type === 'custom_product' || type === 'edit_custom_product') {
    const initValues = productInitValues(data);
    // PGSS-5377: the display rule applies only when EDITING an existing product, where fob = 0 means
    // the user cleared the field. `custom_product` is Copy Product / add-new, where a source product's
    // fob = 0 genuinely means "no usable FOB" and must still fall back to unit cost.
    const resolved = type === 'edit_custom_product' ? resolveFobDisplay(data) : resolveFobCost(data);
    return { ...initValues, unit_cost: checkNullOrUndefined(resolved) ? initValues.unit_cost : resolved };
  }
  if (type === 'product') {
    return productInitValues(data);
  }
  if (type === 'supplierOrderDirectShipment')
    return { products: [] }
  if (type === 'supplierOrder')
    return { products: data }

  return { id: data && data.id ? data.id : null };
}

const baseSchema = {
  name: yup
    .string()
    .required('You must enter a product name')
    .min(5, 'The product name must be at least 5 characters'),
  length: validations('Length'),
  width: validations('Width'),
  height:validations('Height'),
  order_quantity: validations('Order quantity'),
  polish: yup.mixed()
    .required('You must enter a polish')
    .test('is-string-or-object', 'Polish must be either a string or an object', value => {
      // Check if value is an empty string, which should be treated as invalid
      if (value === '') return false;
      // Check if value is either a string or an object
      return typeof value === 'string' || typeof value === 'object';
    }),
  type: yup.mixed()
    .required('You must enter a type')
    .test('is-string-or-object', 'type must be either a string or an object', value => {
      return typeof value === 'string' || typeof value === 'object';
    }),
  stone_color: yup.mixed()
    .required('You must enter a stone color')
    .test('is-string-or-object', 'stone must be either a string or an object', value => {
      return typeof value === 'string' || typeof value === 'object';
    }),
  weight: yup.number().required('You must enter weight').transform((value) => (Number.isNaN(value) ? undefined : value)).nullable(),
}

export const productSchema = yup.object().shape({
  name: yup
    .string()
    .required('You must enter a product name')
    .min(5, 'The product name must be at least 5 characters'),
  weight: yup.string().required('You must enter a weight'),
  length: validations('Length'),
  width: validations('Width'),
  height:validations('Height'),
  unit_price: yup.string().required('Unit price can not be empty'),
  tariff: yup.string().required('You must enter a tariff'),
  shipping: yup.string().required('You must enter a shipping'),
  fob: yup.string().required('You must enter a value'),
  margin: marginValidations(true),
  stone_color: yup
    .object()
    .shape({
      name: yup
        .string()
        .nullable()
        .when('stone_image', {
          is: (value) => value && value.id,
          then: yup.string().required('You must enter a stone_image'),
        }),
    })
    .nullable(),
});

export const customProductSchema = yup.object().shape({...baseSchema});

export const quoteCustomProductSchema = yup.object().shape({
  ...baseSchema,
  unit_cost: yup.number().required('You must enter fob').transform((value) => (Number.isNaN(value) ? undefined : value)).nullable(),
  margin: marginValidations(true),
  unit_price: unitPriceValidations(true)
});

const orderCustomProductSchema = yup.object().shape({
  ...baseSchema,
  margin: marginValidations(true),
  unit_price: unitPriceValidations(true)
});

const directShipmentSchema =
  yup.object().shape({
    selected_supplier: yup.object().required(),
    products: yup.array().of(
      yup.object().shape({
        unit_cost: yup
          .number()
          .min(0, 'Min 0')
          .required('Unit cost is required')
          .transform((value) => (Number.isNaN(value) ? undefined : value)).nullable()
      }))
});

const directShipmentWithQtySchema =
  yup.object().shape({
    selected_supplier: yup.object().required(),
    products: yup.array().of(
      yup.object().shape({
        unit_cost: yup
          .number()
          .min(0, 'Min 0')
          .required('Unit cost is required')
          .transform((value) => (Number.isNaN(value) ? undefined : value)).nullable(),
        quantity: yup
          .number()
          .positive('Quantity must be greater than 0')
          .required('Quantity is required')
          .transform((value) => (Number.isNaN(value) ? undefined : value)).nullable()
      }))
});

const vasOrderSchema = yup.object().shape({
  selected_supplier: yup.object().required(),
  products: yup.array().of(
    yup.object().shape({
      vas: yup.object().shape({
        images: yup.array().nullable(),
        text: yup.array().nullable(),
        imagePrice: yup.number().when('images', {
          is: (value) => Boolean(value && value.length > 0),
          then: yup.number().positive('Etching price must be greater than 0').required('Etching price is required and must be greater than 0'),
          otherwise: yup.number().nullable(),
        }),
        textPrice: yup.number().when('text', {
          is: (value) => Boolean(value && value.length > 0),
          then: yup.number().positive('Sandblasting price must be greater than 0').required('Sandblasting price is required and must be greater than 0'),
          otherwise: yup.number().nullable(),
        }),
      }),
    })
  ),
});

const reOrderSchema = yup.object().shape({
    selected_supplier: yup.object().required(),
    products: yup.array().of(
      yup.object().shape({
        quantity: yup
          .number()
          .positive('Quantity must be greater than 0')
          .required('Quantity is required and must be greater than 0'),
        unit_cost: yup
          .number()
          .nullable()
          .transform((value) => (Number.isNaN(value) || value === '' ? null : value))
          .positive('Unit cost must be greater than 0')
          .required('Unit cost is required and must be greater than 0'),
      }))
});

export const editRFQCustomProductSchema = yup.object().shape({...baseSchema});

export const editCustomProductSchema = yup.object().shape({
  ...baseSchema,
  margin: marginValidations(true),
  unit_price: unitPriceValidations(true)
});

const editConvertToQuote = yup.object().shape({
  ...baseSchema,
  unit_cost: yup.number().required('You must enter fob').transform((value) => (Number.isNaN(value) ? undefined : value)).nullable(),
  margin: marginValidations(true),
  unit_price: unitPriceValidations(true)
});

export const editInventoryProductSchema = yup.object().shape({
  order_quantity: validations('Order quantity')
});

/**+
 * Form Validation Schema
 */
export function getSchema(type, vas, stage, orderType, isCustomProduct = null) {
  if (vas) {
    return vasOrderSchema
  }
  if (type && type === 'product') {
    return productSchema;
  }
  if (type && type === 'custom_product' && stage === 'quote') {
    return quoteCustomProductSchema;
  }
  if (type && type === 'custom_product' && stage === 'order') {
    return orderCustomProductSchema;
  }
  if (type && type === 'custom_product') {
    return customProductSchema;
  }
  if (type && type === 'supplierOrderDirectShipment') {
    return directShipmentSchema;
  }
  if (type && type === 'supplierOrderDirectShipmentwithQty') {
    return directShipmentWithQtySchema;
  }
  if (type && type === 'supplierOrder') {
    return reOrderSchema;
  }
  if (type && type === 'edit_custom_product' && isCustomProduct === false) {
    return editInventoryProductSchema;
  }
  if (type && type === 'edit_custom_product' && orderType === 'convert-to-quote' || stage === 'quote') {
    return editConvertToQuote
  }
  if (type && type === 'edit_custom_product' && stage === 'rfq') {
    return editRFQCustomProductSchema;
  }
  if (type && type === 'edit_custom_product') {
    return editCustomProductSchema;
  }
  return emptySchema;
}